import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Connector, CONNECTOR_TYPE_LABELS } from "@/types";
import {
  Phone,
  MessageCircle,
  Star,
  MapPin,
  Calendar,
  CheckCircle,
  Users,
  Bell,
  Building2,
} from "lucide-react";
import { motion } from "framer-motion";
import { formatDistanceToNow, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface ConnectorCardProps {
  connector: Connector;
  onToggleFavorite: (id: string) => void;
  onMarkFollowUp: (id: string) => void;
  onMarkVisit?: (id: string) => void;
  detailRouteBase?: string;
  creatorName?: string;
  leadsCount?: number;
  pendingRemindersCount?: number;
}

const typeColors: Record<string, string> = {
  broker: "info",
  builder: "accent",
  channel_partner: "warning",
  ca: "success",
  financial_advisor: "gold",
  connector: "secondary",
  dsa: "info",
  hlc: "accent",
  banker: "warning",
  others: "secondary",
};

export function ConnectorCard({
  connector,
  onToggleFavorite,
  onMarkFollowUp,
  onMarkVisit,
  detailRouteBase,
  creatorName,
  leadsCount = 0,
  pendingRemindersCount = 0,
}: ConnectorCardProps) {
  const navigate = useNavigate();
  const isDeactivated = connector.isActive === false;

  // Check if visit is pending (more than 7 days since last visit or never visited)
  const isVisitPending =
    !isDeactivated &&
    (!connector.lastVisitDate ||
      differenceInDays(new Date(), connector.lastVisitDate) > 7);

  // Check if follow-up is pending (more than 7 days since last follow-up or never)
  const isFollowUpPending =
    !isDeactivated &&
    (!connector.lastFollowUpDate ||
      differenceInDays(new Date(), connector.lastFollowUpDate) > 7);

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rawNumber = connector.whatsappNumber.replace(/[^\d]/g, "");
    const number =
      rawNumber.startsWith("91") && rawNumber.length > 10
        ? rawNumber
        : `91${rawNumber}`;
    window.open(`https://wa.me/${number}`);
  };

  const handleCall = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rawNumber = connector.whatsappNumber.replace(/[^\d]/g, "");
    const number =
      rawNumber.startsWith("91") && rawNumber.length > 10
        ? `+${rawNumber}`
        : `+91${rawNumber}`;
    window.open(`tel:${number}`);
  };

  const handleCardClick = () => {
    const base = detailRouteBase || "/tools/channel-partners";
    navigate(`${base}/${connector.id}`);
  };

  const handleVisitClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Visit requires location check - handled in parent
    if (onMarkVisit) {
      onMarkVisit(connector.id);
    } else {
      onMarkFollowUp(connector.id); // fallback to existing behavior
    }
  };

  const handleFollowUpClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkFollowUp(connector.id);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Card
        variant="interactive"
        className={cn(
          "cursor-pointer",
          connector.isFavorite && "border-gold/30 bg-gold/5",
          isDeactivated && "opacity-60 border-red-200 bg-red-50/30",
        )}
        onClick={handleCardClick}
      >
        <CardContent className="p-4">
          {/* Outer layout: Avatar | Main Content | Call/WA buttons */}
          <div className="flex items-stretch gap-3">
            {/* Left: Avatar + counts */}
            <div className="flex-shrink-0 flex flex-col items-center gap-1">
              <div className="relative">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent font-bold text-lg">
                  {connector.name.charAt(0)}
                </div>
                {connector.isFavorite && (
                  <div className="absolute -top-1 -right-1 bg-gold rounded-full p-0.5">
                    <Star className="h-3 w-3 fill-gold-foreground text-gold-foreground" />
                  </div>
                )}
              </div>
              {(leadsCount > 0 || pendingRemindersCount > 0) && (
                <div className="flex flex-col items-center gap-0.5 mt-0.5">
                  {leadsCount > 0 && (
                    <div className="flex items-center gap-0.5 text-xs text-accent">
                      <Users className="h-3 w-3" />
                      <span className="font-semibold text-[11px]">
                        {leadsCount}
                      </span>
                    </div>
                  )}
                  {pendingRemindersCount > 0 && (
                    <div className="flex items-center gap-0.5 text-xs text-orange-500">
                      <Bell className="h-3 w-3" />
                      <span className="font-semibold text-[11px]">
                        {pendingRemindersCount}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Middle: Main content */}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              {/* Name */}
              <div className="min-w-0">
                <h3 className="font-semibold truncate leading-tight">
                  {connector.name}
                </h3>
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                  <Badge variant={typeColors[connector.type] as any}>
                    {CONNECTOR_TYPE_LABELS[connector.type]}
                  </Badge>
                  {creatorName && (
                    <span className="text-xs text-muted-foreground">
                      by {creatorName}
                    </span>
                  )}
                  {isDeactivated && (
                    <Badge
                      variant="destructive"
                      className="text-[10px] px-1.5 py-0"
                    >
                      Deactivated
                    </Badge>
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">
                  {connector.city}, {connector.state}
                  {connector.pincode ? ` - ${connector.pincode}` : ""}
                </span>
              </div>

              {/* Business name + logo — only when provided */}
              {connector.businessName && (
                <div className="flex items-center gap-1 text-xs font-medium text-foreground">
                  <Building2 className="h-3 w-3 flex-shrink-0 text-accent" />
                  <span className="truncate">{connector.businessName}</span>
                </div>
              )}

              {/* Visit & Follow-up info — 2 rows */}
              <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 flex-shrink-0" />
                  Visit:{" "}
                  {connector.lastVisitDate
                    ? formatDistanceToNow(connector.lastVisitDate, {
                        addSuffix: true,
                      })
                    : "Never"}
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="h-3 w-3 flex-shrink-0" />
                  Follow Up:{" "}
                  {connector.lastFollowUpDate
                    ? formatDistanceToNow(connector.lastFollowUpDate, {
                        addSuffix: true,
                      })
                    : "Never"}
                </span>
              </div>

              {/* Visit/FollowUp Action buttons */}
              {!isDeactivated && (
                <div className="flex items-center gap-1.5 pt-0.5">
                  <Button
                    variant={isVisitPending ? "destructive" : "accent"}
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={handleVisitClick}
                  >
                    {isVisitPending ? "Pending Visit" : "Visit Done"}
                  </Button>
                  <Button
                    variant={isFollowUpPending ? "destructive" : "accent"}
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={handleFollowUpClick}
                  >
                    {isFollowUpPending ? "Pending Follow Up" : "Follow Up Done"}
                  </Button>
                </div>
              )}
            </div>

            {/* Right: Star (top) + Call / WhatsApp buttons (bottom) — fixed column */}
            <div className="flex-shrink-0 flex flex-col items-center justify-between">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(connector.id);
                }}
                className={cn(connector.isFavorite && "text-gold")}
                title={connector.isFavorite ? "Remove from favorites" : "Add to favorites"}
                aria-label={connector.isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Star
                  className={cn(
                    "h-4 w-4",
                    connector.isFavorite && "fill-gold",
                  )}
                />
              </Button>
              <div className="flex flex-col items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full shadow-sm"
                  onClick={handleCall}
                  title="Call"
                >
                  <Phone className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full shadow-sm"
                  onClick={handleWhatsApp}
                  title="WhatsApp"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
